import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { HeardOfDialog } from "./HeardOfDialog";
import { SentimentButtons } from "../left-panel/SentimentButtons";
import { CenterPanelContainer, FROSTED_GLASS } from "./styles";
import { COLUMN_HEIGHT } from "../../constants";
import { FlipIcon } from "../icons/FlipIcon";
import type { MSHatData, HatSentiment } from "../../types";

interface CenterPanelProps {
  hat: MSHatData;
  heardOf?: boolean | null;
  onHeardOfChange?: (heardOf: boolean) => void;
  onFlip?: () => void;
  clusterColor?: string;
  hatSentiment?: HatSentiment;
  onHatSentimentChange?: (sentiment: HatSentiment) => void;
}

const CardContainer = styled(Box, {
  label: "HatCardContainer",
  shouldForwardProp: (prop) => prop !== "borderColor",
})<{ borderColor?: string }>(({ borderColor }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: 16,
  overflow: "hidden",
  isolation: "isolate",
  border: "4px solid transparent",
  transition: "border-color 0.2s ease",
  boxShadow:
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)",
  ...(borderColor && {
    "&:has(.HatCardFlipButton-root:hover)": {
      borderColor,
    },
  }),
}));

const CardImage = styled("img", {
  label: "HatCardImage",
})(() => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
}));

const GradientOverlay = styled(Box, {
  label: "HatCardGradient",
})(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background:
    "linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.9) 20%, transparent 60%)",
  pointerEvents: "none",
}));

// Outer: handles absolute positioning + horizontal centering via translateX(-50%).
// Inner: handles scaling. Splitting these avoids a Firefox bug where combining
// translateX(-50%) with scale(calc(...)) in a single transform causes incorrect rendering.
const FlipButtonPositioner = styled(Box, {
  label: "HatCardFlipButtonPositioner",
})(() => ({
  position: "absolute",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
}));

const FlipButton = styled(Box, {
  label: "HatCardFlipButton",
})(() => ({
  width: 64,
  height: 64,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.32)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  transform: `scale(calc(${COLUMN_HEIGHT} / 569px))`,
  transformOrigin: "bottom center",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.48)",
  },
}));

FlipButton.defaultProps = { className: "HatCardFlipButton-root" };

const SentimentPillPositioner = styled(Box, {
  label: "SentimentPillPositioner",
})(() => ({
  position: "absolute",
  top: 24,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 2,
}));

const SentimentPill = styled(Box, {
  label: "SentimentPill",
})(() => ({
  ...FROSTED_GLASS,
  borderRadius: 40,
  padding: "8px 32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transform: `scale(calc(${COLUMN_HEIGHT} / 569px))`,
  transformOrigin: "top center",
}));

const FlipLabel = styled(Typography, {
  label: "FlipButtonLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  textAlign: "center",
}));

export const CenterPanel = ({
  hat,
  heardOf,
  onHeardOfChange,
  onFlip,
  clusterColor,
  hatSentiment,
  onHatSentimentChange,
}: CenterPanelProps) => {
  const imageUrl = hat.hatCard?.largeCard || hat.displayImage?.url;

  return (
    <CenterPanelContainer>
      <CardContainer borderColor={clusterColor}>
        {imageUrl && <CardImage src={imageUrl} alt={hat.displayName} />}
        <GradientOverlay />

        <SentimentPillPositioner>
          <SentimentPill>
            <SentimentButtons
              sentiment={hatSentiment}
              onChange={onHatSentimentChange}
            />
          </SentimentPill>
        </SentimentPillPositioner>

        <FlipButtonPositioner>
          <FlipButton onClick={onFlip}>
            <FlipIcon />
            <FlipLabel>Flip</FlipLabel>
          </FlipButton>
        </FlipButtonPositioner>

        {heardOf === null && onHeardOfChange && (
          <HeardOfDialog onAnswer={onHeardOfChange} />
        )}
      </CardContainer>
    </CenterPanelContainer>
  );
};
